import * as React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button } from '@mui/material';
import Cropper from 'cropperjs';
import { imageFileAsDataUrl, basename } from '../../file';
import { DeferredPromise, defer } from '@crazygames/utils/deferredPromise';
import { COLORS } from '../../Styleguide/Common/colors';
import { StyledBodyText } from '../../Styleguide/Common/Text';

const StyledCropContainer = styled('div')(({ theme: { spacing } }) => ({
  maxWidth: '100%',
  marginLeft: spacing(3),
  marginRight: spacing(3),
}));

const StyledCropImage = styled('img')(() => ({
  maxWidth: 800,
  maxHeight: 600,
}));

export interface Ratio {
  width: number;
  height: number;
}

export interface CzyCropperProps {
  file: File;
  ratio: Ratio;
  onCrop: (file: File) => void;
  minWidth?: number;
  minHeight?: number;
  isNonEditable?: boolean;
  noteText?: React.ReactNode;
}

class CzyCropper extends React.Component<CzyCropperProps> {
  private editorRef: React.RefObject<HTMLImageElement>;

  private cropper: DeferredPromise<Cropper>;

  constructor(props: CzyCropperProps) {
    super(props);
    this.cropper = defer<Cropper>();
    this.editorRef = React.createRef();
  }

  componentDidMount() {
    const img = this.editorRef.current;
    if (!img) {
      throw new Error(`[CzyCropper] img ref is undefined`);
    }
    this.initializeCropper(img);
  }

  render() {
    const { noteText, isNonEditable } = this.props;
    const hasNote = !!noteText;

    return (
      <Box>
        <StyledCropContainer>
          <StyledCropImage ref={this.editorRef} alt="cropper" />
        </StyledCropContainer>
        <Box sx={{ position: 'relative', bottom: 0, left: 0, right: 0, textAlign: 'center', p: 2, backgroundColor: COLORS.black[70] }}>
          {hasNote && (
            <StyledBodyText color="white30" sx={{ mb: 2 }}>
              {noteText}
            </StyledBodyText>
          )}
          <Button variant="contained" color="primary" onClick={this.onCrop} disabled={isNonEditable}>
            Submit
          </Button>
        </Box>
      </Box>
    );
  }

  private async initializeCropper(img: HTMLImageElement) {
    const { file, ratio, minWidth, minHeight } = this.props;
    const b64Img = await imageFileAsDataUrl(file);
    img.src = b64Img;
    const cropper = new Cropper(img, {
      aspectRatio: ratio.width / ratio.height,
      viewMode: 1, //  restrict the crop box to not exceed the size of the canvas.
      crop: (event) => {
        const { width, height } = event.detail;
        const newWidth = minWidth === undefined ? width : Math.max(width, minWidth);
        const newHeight = minHeight === undefined ? height : Math.max(height, minHeight);
        if (newWidth !== width && newHeight !== height) {
          cropper.setData({
            width: newWidth,
            height: newHeight,
          });
        }
      },
    });
    this.cropper.resolve(cropper);
  }

  private onCrop = async () => {
    const file = await this.getCroppedFile();
    this.props.onCrop(file);
  };

  private async getCroppedBlob(): Promise<Blob> {
    const cropper = await this.cropper.promise;
    return new Promise<Blob>((resolve, reject) => {
      const croppedCanvas = cropper.getCroppedCanvas({});

      if (!croppedCanvas) {
        reject('Could not get cropped canvas');
      }

      croppedCanvas.toBlob((blob) => {
        if (!blob) {
          reject('Could not blob-ify cropped canvas');
        } else {
          resolve(blob);
        }
      }, 'image/png');
    });
  }

  private async getCroppedFile(): Promise<File> {
    const { file, ratio } = this.props;
    const fileName = `${basename(file.name)}_${ratio.width}x${ratio.height}.png`;
    const blob = await this.getCroppedBlob();
    return new File([blob], fileName);
  }
}

export default CzyCropper;
